import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Users, ArrowLeft, RefreshCw, Loader2, Calendar, FileText,
  UserCheck, UserX, Search, ChevronRight, Cake, ClockAlert, TrendingUp,
  Wallet, TriangleAlert, Bug,
} from "lucide-react";

/**
 * Per-client KeyPay dashboard. Lists every employee (active +
 * terminated), shows headline KPIs, recent payruns, and routes into
 * an employee detail page for the full record.
 */
export default function KeyPayClientDashboard() {
  const { clientId } = useParams();
  const [summary, setSummary] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [employeesMeta, setEmployeesMeta] = useState({});
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("active");
  const [search, setSearch] = useState("");
  const [clientName, setClientName] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s, e, ev] = await Promise.all([
        api.get(`/clients/${clientId}`).catch(() => ({ data: {} })),
        api.get(`/integrations/keypay/clients/${clientId}/summary`),
        api.get(`/integrations/keypay/clients/${clientId}/employees`),
        api.get(`/integrations/keypay/clients/${clientId}/upcoming-events?days=30`)
          .catch(() => ({ data: { events: [], birthdays: 0, anniversaries: 0 } })),
      ]);
      setClientName(c.data?.company_name || clientId);
      setSummary(s.data);
      setEmployees(e.data?.employees || []);
      setEmployeesMeta({
        terminated_endpoint_error: e.data?.terminated_endpoint_error,
        raw_active_count: e.data?.raw_active_count,
        raw_terminated_count: e.data?.raw_terminated_count,
      });
      setEvents(ev.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load KeyPay data");
    } finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (employees || []).filter((e) => {
      if (tab === "active" && !e.is_active) return false;
      if (tab === "terminated" && e.is_active) return false;
      if (!q) return true;
      return [e.name, e.email, e.job_title, e.external_id]
        .some((v) => (v || "").toLowerCase().includes(q));
    });
  }, [employees, tab, search]);

  return (
    <div className="p-6 max-w-7xl" data-testid="keypay-client-dashboard">
      <Link to="/integrations/keypay"
        className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900 mb-3"
        data-testid="keypay-back">
        <ArrowLeft className="h-3 w-3" /> Back to KeyPay
      </Link>

      <div className="flex items-center gap-3 mb-6">
        <div className="h-12 w-12 grid place-items-center rounded-sm bg-purple-100 text-purple-700">
          <Users className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <div className="label-overline">KEYPAY · PAYROLL DASHBOARD</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">
            {clientName}
          </h1>
        </div>
        <Button size="sm" variant="outline" className="rounded-sm"
          onClick={load} disabled={loading}
          data-testid="keypay-refresh">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                   : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
          Refresh
        </Button>
        <Button size="sm" variant="outline" className="rounded-sm"
          onClick={async () => {
            try {
              const { data } = await api.get(
                `/integrations/keypay/clients/${clientId}/employees/debug`);
              const w = window.open("", "_blank");
              if (w) {
                w.document.write(`<pre style="font-family:monospace;padding:20px;font-size:12px;white-space:pre-wrap">${
                  JSON.stringify(data, null, 2).replace(/</g, "&lt;")
                }</pre>`);
                w.document.title = `KeyPay employees debug`;
              }
            } catch (e) { toast.error(e.response?.data?.detail || "Debug fetch failed"); }
          }}
          data-testid="keypay-debug-btn">
          <Bug className="h-3.5 w-3.5 mr-1.5" />Debug
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <Pill label="Active employees" value={summary?.active_count ?? "—"}
          icon={UserCheck} accent="text-emerald-700"
          testid="keypay-kpi-active" />
        <Pill label="Past employees" value={summary?.terminated_count ?? "—"}
          icon={UserX} accent="text-zinc-700"
          testid="keypay-kpi-terminated" />
        <Pill label="Total headcount" value={summary?.total_headcount ?? "—"}
          icon={Users} testid="keypay-kpi-total" />
        <Pill label="Open leave" value={summary?.pending_leave_count ?? "—"}
          icon={Calendar}
          accent={(summary?.pending_leave_count ?? 0) > 0 ? "text-amber-700" : ""}
          testid="keypay-kpi-leave" />
      </div>

      {/* Quick action buttons — Leave calendar / Timesheets / Pay-changes */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Link to={`/integrations/keypay/clients/${clientId}/leave-calendar`}
          data-testid="keypay-action-leave-calendar">
          <Button size="sm" variant="outline" className="rounded-sm">
            <Calendar className="h-3.5 w-3.5 mr-1.5" /> Leave calendar
          </Button>
        </Link>
        <Link to={`/integrations/keypay/clients/${clientId}/timesheets`}
          data-testid="keypay-action-timesheets">
          <Button size="sm" variant="outline" className="rounded-sm">
            <ClockAlert className="h-3.5 w-3.5 mr-1.5" /> Timesheets
          </Button>
        </Link>
        <Link to={`/integrations/keypay/clients/${clientId}/pay-changes`}
          data-testid="keypay-action-pay-changes">
          <Button size="sm" variant="outline" className="rounded-sm">
            <TrendingUp className="h-3.5 w-3.5 mr-1.5" /> Pay-rate changes
          </Button>
        </Link>
        <Link to={`/integrations/keypay/clients/${clientId}/super-payments`}
          data-testid="keypay-action-super-payments">
          <Button size="sm" variant="outline" className="rounded-sm">
            <Wallet className="h-3.5 w-3.5 mr-1.5" /> Super payments
          </Button>
        </Link>
      </div>

      {employeesMeta?.terminated_endpoint_error && (
        <div className="mb-4 rounded-sm border border-amber-300 bg-amber-50 p-2.5 text-xs flex items-start gap-2"
          data-testid="keypay-term-warning">
          <TriangleAlert className="h-3.5 w-3.5 text-amber-700 shrink-0 mt-0.5" />
          <div>
            <b>Terminated-employee fetch degraded.</b> {employeesMeta.terminated_endpoint_error}
            {" "}Click <b>Debug</b> above to see the raw KeyPay responses.
          </div>
        </div>
      )}

      {/* Birthdays + Anniversaries widget */}
      {(events?.events || []).length > 0 && (
        <Card className="rounded-sm border-zinc-200 mb-4 bg-gradient-to-br from-pink-50/40 to-white"
          data-testid="keypay-events-widget">
          <CardContent className="p-0">
            <div className="px-4 py-2 border-b border-zinc-200 bg-zinc-50 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-600 flex items-center gap-1.5">
              <Cake className="h-3 w-3" /> Birthdays &amp; anniversaries · next 30 days
              <span className="ml-auto font-mono text-zinc-500 normal-case tracking-normal text-[10px]">
                🎂 {events?.birthdays ?? 0} · 🎉 {events?.anniversaries ?? 0}
              </span>
            </div>
            <div className="divide-y divide-zinc-100">
              {(events.events || []).slice(0, 8).map((e, i) => (
                <Link key={`${e.employee_id}-${e.kind}-${i}`}
                  to={`/integrations/keypay/clients/${clientId}/employees/${e.employee_id}`}
                  className="grid grid-cols-[100px_140px_1fr_80px] gap-3 px-4 py-2 items-center text-sm hover:bg-zinc-50"
                  data-testid={`keypay-event-${e.employee_id}-${e.kind}`}>
                  <span className="font-mono text-xs text-zinc-600">{e.date}</span>
                  <Badge variant="outline" className={`rounded-sm text-[10px] w-fit ${
                    e.kind === "birthday"
                      ? "bg-pink-50 text-pink-700 border-pink-200"
                      : "bg-emerald-50 text-emerald-700 border-emerald-200"
                  }`}>
                    {e.kind === "birthday" ? "🎂 Birthday" : "🎉 Anniversary"}
                    {e.years ? ` · ${e.years}y` : ""}
                  </Badge>
                  <span className="truncate font-semibold">
                    {e.employee_name}
                    {e.job_title && <span className="text-zinc-500 font-normal ml-1.5">· {e.job_title}</span>}
                  </span>
                  <span className="text-xs text-zinc-500 text-right font-mono">
                    {e.is_today ? "today" : `in ${e.days_away}d`}
                  </span>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent payruns */}
      {summary?.recent_payruns?.length > 0 && (
        <Card className="rounded-sm border-zinc-200 mb-4"
          data-testid="keypay-recent-payruns">
          <CardContent className="p-0">
            <div className="px-4 py-2 border-b border-zinc-200 bg-zinc-50 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-600 flex items-center gap-1.5">
              <FileText className="h-3 w-3" /> Recent payruns
            </div>
            {summary.recent_payruns.map((p) => (
              <Link key={p.id}
                to={`/integrations/keypay/clients/${clientId}/payruns/${p.id}`}
                className="grid grid-cols-[1fr_140px_140px_120px_80px_30px] gap-2 px-4 py-2 border-b border-zinc-100 last:border-b-0 text-sm items-center hover:bg-zinc-50"
                data-testid={`keypay-payrun-link-${p.id}`}>
                <span className="font-semibold">
                  Pay run #{p.id}
                  {p.isFinalised && (
                    <Badge className="ml-2 rounded-sm text-[9px] bg-emerald-100 text-emerald-800 border-emerald-200">FINALISED</Badge>
                  )}
                </span>
                <span className="text-xs text-zinc-600">
                  Paid: {fmtDate(p.datePaid)}
                </span>
                <span className="text-xs text-zinc-600">
                  Period end: {fmtDate(p.payPeriodEnding)}
                </span>
                <span className="text-xs font-mono text-right">
                  ${(p.totalPayments || 0).toLocaleString()}
                </span>
                <span className="text-xs text-zinc-500 text-right">
                  {p.totalEmployees} ppl
                </span>
                <ChevronRight className="h-3.5 w-3.5 text-zinc-400 justify-self-end" />
              </Link>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Employees tabs + search */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <TabBtn active={tab === "active"} onClick={() => setTab("active")}
          label="Current" count={summary?.active_count} testid="keypay-tab-active" />
        <TabBtn active={tab === "terminated"} onClick={() => setTab("terminated")}
          label="Past" count={summary?.terminated_count} testid="keypay-tab-terminated" />
        <TabBtn active={tab === "all"} onClick={() => setTab("all")}
          label="All" count={summary?.total_headcount} testid="keypay-tab-all" />
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, email, job title…"
              className="h-9 pl-7 text-sm"
              data-testid="keypay-search" />
          </div>
        </div>
      </div>

      {/* Employee list */}
      <Card className="rounded-sm border-zinc-200" data-testid="keypay-employees">
        <CardContent className="p-0">
          <div className="grid grid-cols-[1.2fr_1.5fr_140px_120px_30px] gap-2 px-3 py-2 bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-600">
            <span>Name</span>
            <span>Email · Job title</span>
            <span>Status</span>
            <span>Start date</span>
            <span />
          </div>
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-sm italic text-zinc-500">
              {search ? "No employees match this search." : "No employees in this tab."}
            </div>
          ) : filtered.map((e) => (
            <Link key={e.id}
              to={`/integrations/keypay/clients/${clientId}/employees/${e.id}`}
              className="grid grid-cols-[1.2fr_1.5fr_140px_120px_30px] gap-2 px-3 py-2 border-b border-zinc-100 last:border-b-0 text-sm items-center hover:bg-zinc-50 cursor-pointer"
              data-testid={`keypay-employee-${e.id}`}>
              <span className="truncate font-semibold">{e.name}</span>
              <span className="truncate text-xs text-zinc-600">
                {e.email || <span className="italic text-zinc-400">no email</span>}
                {e.job_title && <span className="ml-2 text-zinc-500">· {e.job_title}</span>}
              </span>
              <span>
                <Badge variant="outline"
                  className={`rounded-sm text-[9px] ${e.is_active
                    ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                    : "bg-zinc-100 text-zinc-600 border-zinc-200"}`}>
                  {e.is_active ? "ACTIVE" : "TERMINATED"}
                </Badge>
              </span>
              <span className="text-xs font-mono text-zinc-600">{fmtDate(e.start_date)}</span>
              <ChevronRight className="h-3.5 w-3.5 text-zinc-400" />
            </Link>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function Pill({ label, value, icon: Icon, accent, testid }) {
  return (
    <div className="border border-zinc-200 rounded-sm p-3 bg-white" data-testid={testid}>
      <div className="label-overline text-[9px] flex items-center gap-1">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </div>
      <div className={`font-display font-bold text-xl mt-1 ${accent || ""}`}>{value}</div>
    </div>
  );
}

function TabBtn({ active, onClick, label, count, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`px-3 h-9 rounded-sm border font-display font-bold text-sm inline-flex items-center gap-1.5 transition ${
        active ? "bg-zinc-900 text-white border-zinc-900"
               : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
      }`}>
      {label}
      {count !== undefined && (
        <Badge className={`rounded-sm text-[9px] ${
          active ? "bg-white/20 text-white border-white/30"
                 : "bg-zinc-100 text-zinc-600 border-zinc-200"}`}>{count}</Badge>
      )}
    </button>
  );
}

function fmtDate(s) {
  if (!s) return "—";
  return String(s).slice(0, 10);
}
